<template>
  <el-popover v-model="popVisible" placement="bottom-start" :width="popWidth" trigger="click" popper-class="owned-branch-tree">
    <element-input
      slot="reference"
      ref="input"
      readonly
      :value="valueLabel"
      :title="valueTip"
      :style="{
        width: width ? width + 'px' : '',
        minWidth: minWidth ? minWidth + 'px' : '',
        maxWidth: maxWidth ? maxWidth + 'px' : ''
      }"
    >
      <i
        slot="suffix"
        :class="popVisible ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
        style="width: 25px;line-height: 28px;font-size: 14px;cursor: pointer;"
      />
    </element-input>
    <el-scrollbar :style="{height:treeHeight}">
      <el-tree
        ref="tree"
        :data="options"
        :props="{ children: 'children', label: 'label' }"
        node-key="value"
        :default-expanded-keys="defaultExpandedKeys"
        :current-node-key="value"
        highlight-current
        :expand-on-click-node="false"
        @node-click="handleNodeClick"
      >
        <template slot-scope="{ node, data }">
          <span
            :title="data.tip"
            :style="selectedNodeKey === data.value ? 'color: #1F75D5;font-weight: bold;' : ''"
          >
            {{ node.label }}
          </span>
        </template>
      </el-tree>
    </el-scrollbar>
  </el-popover>
</template>

<script>
import { Input as ElementInput } from 'element-ui'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import { getObjectByCache } from '@/utils/basicDataCache'
import { sortByMultiField } from '@/utils'

export default {
  name: 'OwnedBranchTree',
  components: { ElementInput },
  props: {
    officeList: { type: Array, required: true },
    value: { type: Number, required: true },
    widthAutoFit: { type: Boolean, default: false },
    minWidth: { type: Number, default: 0 },
    maxWidth: { type: Number, default: 0 }
  },
  data() {
    return {
      settingLabel: '',
      options: [],
      valueLabel: '',
      valueTip: '',
      width: 0,
      popVisible: false,
      popWidth: 160,
      defaultExpandedKeys: [],
      selectedNodeKey: '',
      treeHeight: '400px'
    }
  },
  watch: {
    '$store.state.settings.partnerLabelSettings': {
      handler: function() {
        this.readSettingLabel()
        this.init()
      }
    },
    officeList() {
      this.init()
    },
    value(newVal, oldVal) {
      const newIndex = this.officeList.findIndex(p => p.officeId === newVal)
      const oldIndex = this.officeList.findIndex(p => p.officeId === oldVal)
      if (newIndex < 0 || oldIndex < 0) {
        this.init()
      } else {
        this.handValueChange()
      }
    },
    popVisible(newVal) {
      if (newVal) {
        this.selectedNodeKey = this.value
      } else {
        this.$emit('input', this.selectedNodeKey)
      }
    }
  },
  created() {
    this.readSettingLabel()
    this.init()
  },
  methods: {
    readSettingLabel() {
      const configs = this.$store.state.settings.partnerLabelSettings
      const curConfig = configs.find(config => config.autoKey === 'auto_office')
      this.settingLabel = Object.keys(curConfig).find(key => typeof curConfig[key] === 'boolean' && curConfig[key])
    },
    init() {
      const officeList = [...this.officeList]
      const index = officeList.findIndex(p => p.officeId === this.value)
      if (index < 0) {
        const obj = getObjectByCache(TYPE_NAMES.basic_data_Office_All, 'officeId', this.value)
        if (obj) {
          officeList.push(obj)
        }
      }
      this.options = []
      officeList.sort((a, b) => {
        const aTime = new Date(a.dateCreated).getTime()
        const bTime = new Date(b.dateCreated).getTime()
        return aTime - bTime
      })
      sortByMultiField(officeList, ['level', 'code'])
      const optionMap = new Map()
      for (const office of officeList) {
        const option = { value: office.officeId, label: this.getLabel(office), tip: this.getLabelTip(office) }
        optionMap.set(office.uuid, option)
        if (!office.parentBranchUuid || !optionMap.has(office.parentBranchUuid)) {
          this.options.push(option)
        } else {
          const parentOption = optionMap.get(office.parentBranchUuid)
          if (!parentOption.children) {
            parentOption.children = []
          }
          parentOption.children.push(option)
        }
      }
      this.defaultExpandedKeys = []
      for (const option of this.options) {
        this.defaultExpandedKeys.push(option.value)
      }
      this.popWidth = this.caculatePopWidth(this.options)
      this.resetTreeHeight(officeList.length)
      this.handValueChange()
    },
    caculatePopWidth(options, iconNum = 1) {
      let maxWidth = 256
      for (const option of options) {
        const width = 224 + iconNum * 32
        if (width > maxWidth) {
          maxWidth = width
        }
        if (option.children) {
          const childWidth = this.caculatePopWidth(option.children, iconNum + 1)
          if (childWidth > maxWidth) {
            maxWidth = childWidth
          }
        }
      }
      return maxWidth
    },
    resetTreeHeight(count) {
      let newHeight = count * 26
      if (newHeight > 400) {
        newHeight = 400
      }
      this.treeHeight = `${newHeight}px`
    },
    handValueChange() {
      const office = getObjectByCache(TYPE_NAMES.basic_data_Office_All, 'officeId', this.value)
      this.valueLabel = office ? this.getLabel(office) : ''
      const textWidth = this.getTextWidth(this.valueLabel, 12, 25)
      this.width = textWidth + 15
      this.$nextTick(() => {
        const input = this.$refs.input.$refs.input
        input.value = this.getTextEllipsis(this.valueLabel, input.offsetWidth)
      })
      /* 提示 */
      this.valueTip = office ? this.getLabelTip(office) : ''
      this.$emit('tipChange', this.valueTip)
    },
    getLabel(office) {
      let txt = ''
      switch (this.settingLabel) {
        case 'chineseName':
          txt = office.chineseName
          break
        case 'englishName':
          txt = office.englishName
          break
        default:
          txt = office.code
          break
      }
      return txt
    },
    getLabelTip(office) {
      let tip = ''
      switch (this.settingLabel) {
        case 'chineseName':
          tip = office.code
          break
        case 'englishName':
          tip = office.code
          break
        default:
          tip = this.$getLangColumn(office)
          break
      }
      return tip
    },
    getTextEllipsis(text, width, fontSize = 12, paddingWidth = 25) {
      if (width > 0) {
        const textWidth = this.getTextWidth(text, fontSize, paddingWidth)
        if (textWidth > width) {
          for (let i = 3; i <= text.length; ++i) {
            const subText = text.substring(0, i)
            const subTextWidth = this.getTextWidth(subText, fontSize, paddingWidth)
            if (subTextWidth > width) {
              return text.substring(0, i - 2) + '...'
            }
          }
        }
      }
      return text
    },
    getTextWidth(text, fontSize = 12, paddingWidth = 25) {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      ctx.font = `normal ${fontSize}px sans-serif`
      return ctx.measureText(text).width + paddingWidth
    },
    handleNodeClick(data) {
      this.selectedNodeKey = data.value
      this.popVisible = false
    }
  }
}
</script>

<style lang="scss">
.owned-branch-tree.el-popover {
  padding: 2px;
  .el-scrollbar {
    .el-scrollbar__wrap {
      overflow-x: hidden;
      .el-tree {
        .el-tree-node {
          width: max-content;
          min-width: 100%;
        }
        .el-tree-node__content {
          .el-tree-node__expand-icon {
            font-size: 20px;
            padding: 2px 6px;
          }
        }
      }
    }
    .el-scrollbar__bar.is-horizontal {
      height: 12px;
    }
  }
}
</style>
